import React, { useEffect, useState, useMemo } from "react";
import {
  Flex,
  List,
  Modal,
  Typography,
} from "antd";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { formatSingleChat } from "../../../utils";
import { setCurrentChat } from "../../../redux/slices/chatSlice";
import CustomLoader from "../UI/Loader";
import ENDPOINTS from "../../../services/endpoint";
import apiInstance from "../../../services/apiService";
import ContactCard from "../../Chat/ContactCard";
import { Conversation as Contact } from "../../../redux/slices/contactsSlice";
import "./index.scss";

const { Text } = Typography;

interface ContactListProps {
  searchQuery?: string;
}

const ContactList: React.FC<ContactListProps> = ({ searchQuery }) => {
  const dispatch = useAppDispatch();
  const { chats } = useSelector((state: RootState) => state.chat);
  const { contacts: contactList } = useSelector(
    (state: RootState) => state.contacts
  );
  const [phoneNumbersLoading, setPhoneNumbersLoading] =
    useState<boolean>(false);
  const [phoneNumbersModalVisible, setPhoneNumbersModalVisible] =
    useState<boolean>(false);
  const [phoneNumbersList, setPhoneNumbersList] = useState<any>(null);
  const [selectedContactInfo, setSelectedContactInfo] =
    useState<Contact | null>(null);

  // Derive contact lists using useMemo to avoid flickers and redundant calculations
  const { efoneContactsList, nonEfoneContactsList } = useMemo(() => {
    if (!contactList) return { efoneContactsList: [], nonEfoneContactsList: [] };
    
    const efone = contactList.filter((contact: Contact) => contact.iseFone);
    const nonEfone = contactList.filter((contact: Contact) => !contact.iseFone);
    
    return { efoneContactsList: efone, nonEfoneContactsList: nonEfone };
  }, [contactList]);

  const filterFn = (contact: Contact) => {
    const search = searchQuery?.toLowerCase() || "";
    const name = contact.fullName?.toLowerCase() || "";
    const number = contact.number?.toLowerCase() || "";
    return name.includes(search) || number.includes(search);
  };

  const filteredEfoneContacts = useMemo(() => 
    efoneContactsList.filter(filterFn), 
    [efoneContactsList, searchQuery]
  );
  
  const filteredNonEfoneContacts = useMemo(() => 
    nonEfoneContactsList.filter(filterFn), 
    [nonEfoneContactsList, searchQuery]
  );

  const handleCreateDummyChat = (phoneNumberDetails: any) => {
    const newChat = {
      _id: selectedContactInfo?.id,
      isGroup: false,
      groupMembers: [],
      lastMessage: "",
      iseFone: selectedContactInfo?.iseFone,
      mediaType: null,
      filePath: null,
      firstId: null,
      secondId: null,
      firstUserName: null,
      secondUserName: null,
      name: selectedContactInfo?.fullName,
      firstUserPhoneNumber: phoneNumberDetails?.phoneNumber,
      secondUserPhoneNumber: null,
      lastUpdate: null,
      lastMsgId: null,
      groupSetting: {
        addOthersMembers: true,
        editGroup: true,
        sendMessage: true,
      },
      pastMembers: [],
      lastUpdateToSort: null,
      sourceNumber: selectedContactInfo?.number,
      block: null,
      sourceUserName: null,
      isPin: null,
      unreadCount: 0,
      isMuted: null,
      isArchived: null,
      isNewChat: true,
      savedName:
        selectedContactInfo?.fullName || selectedContactInfo?.number,
    };

    const formattedChat = formatSingleChat(newChat);
    dispatch(setCurrentChat(formattedChat));
  };

  const handleContactClick = (contact: Contact) => {
    if (!contact.iseFone) {
      toast.error("You cannot text a non-efone number");
      return;
    }

    const chat = chats.find((convo: any) => {
      return convo.sourceNumber === contact.number;
    });

    if (chat) {
      dispatch(setCurrentChat(chat));
    } else {
      setSelectedContactInfo(contact);
      setPhoneNumbersModalVisible(true);
    }
  };

  const getPhoneNumbersList = async () => {
    const URL = ENDPOINTS.GET_PHONE_NUMBERS;

    setPhoneNumbersLoading(true);
    try {
      const response = await apiInstance.get(URL);
      const data = response?.data?.data;
      setPhoneNumbersList(data);
    } catch (err: any) {
      toast.error(err.message || "Couldn't get Phone Numbers");
    } finally {
      setPhoneNumbersLoading(false);
    }
  };

  useEffect(() => {
    if (phoneNumbersModalVisible) {
      getPhoneNumbersList();
    }
  }, [phoneNumbersModalVisible]);

  const isLoading = !contactList || contactList.length === 0;

  return (
    <>
      <div className="new-chats-container">
        <div className="efone-contacts-container">
          <div className="efone-contacts-list">
            <List
              className="contacts-wrapper-list"
              loading={{
                spinning: isLoading,
                indicator: (
                  <Flex
                    className="w-full h-full"
                    justify="center"
                    align="center"
                  >
                    <CustomLoader />
                  </Flex>
                ),
              }}
            >
              {!isLoading && (
                <>
                  <div className="banner">eFone Contacts</div>
                  <List
                    dataSource={filteredEfoneContacts}
                    renderItem={(contact: Contact) => (
                      <ContactCard
                        onClick={() => handleContactClick(contact)}
                        key={contact.id}
                        userName={contact.fullName || ""}
                        phoneNumber={contact.number || ""}
                        isEfone={contact.iseFone}
                      />
                    )}
                  />

                  <div className="banner">Invite to eFone</div>
                  <List
                    dataSource={filteredNonEfoneContacts}
                    renderItem={(contact: Contact) => (
                      <ContactCard
                        onClick={() => handleContactClick(contact)}
                        key={contact.id}
                        userName={contact.fullName || ""}
                        phoneNumber={contact.number || ""}
                        isEfone={false}
                      />
                    )}
                  />
                </>
              )}
            </List>
          </div>
        </div>

        <Modal
          centered
          className="custom-modal numbers-list-modal"
          open={phoneNumbersModalVisible}
          onCancel={() => setPhoneNumbersModalVisible(false)}
          title="Select Phone Number"
          footer={null}
        >
          <List
            className="numbers-list"
            dataSource={phoneNumbersList || []}
            loading={{
              spinning: phoneNumbersLoading,
              indicator: <CustomLoader />,
            }}
            renderItem={(item: any) => (
              <List.Item
                key={item?._id}
                className="number"
                onClick={() => {
                  handleCreateDummyChat(item);
                  setPhoneNumbersModalVisible(false);
                }}
              >
                <Text className="number">{item?.phoneNumber}</Text>
              </List.Item>
            )}
          />
        </Modal>
      </div>
    </>
  );
};

export default ContactList;
